'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import {
  Star, MapPin, Languages, CheckCircle2, Clock, ChevronLeft, ChevronRight,
  Award, BookOpen, Flame, Camera, Quote, ArrowLeft, Phone, Video, Calendar } from
'lucide-react';
import HomeNavbar from '@/app/home-screen/components/HomeNavbar';
import HomeBottomNav from '@/app/home-screen/components/HomeBottomNav';
import { PRIESTS_DATA } from '../page';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PRIEST_DETAILS: Record<string, {
  biography: string;
  qualifications: string[];
  ritualExpertise: {name: string;description: string;icon: string;}[];
  reviews: {name: string;location: string;rating: number;text: string;ritual: string;date: string;}[];
  gallery: {src: string;alt: string;}[];
  availableSlots: Record<string, string[]>;
}> = {
  'raghavendra-sharma': {
    biography: `Pandit Raghavendra Sharma was born into a family of Vedic scholars in Udupi, Karnataka, where he began his Vedic education at the age of seven under the guidance of his grandfather. He completed a rigorous 12-year residential study at the Sringeri Sharada Peetham, mastering the Yajur Veda, Grihya Sutras, and Dharmashastra. Over 28 years of active practice, he has performed over 1,240 ceremonies — from intimate household pujas to elaborate multi-day wedding rituals. He is known for his melodious Sanskrit recitations, patient explanations of ritual significance, and his ability to honor regional family traditions while maintaining Vedic authenticity. He has also trained 14 junior priests and conducts monthly Vedic study circles in Bengaluru.`,
    qualifications: [
    'Veda Bhushana — Sringeri Sharada Peetham (1998)',
    'Agama Shastra Visharada — Mysore Sanskrit University (2001)',
    'Certified Vedic Practitioner — All India Brahmin Federation (2005)',
    '12-year residential Vedic education under Guru Parampara',
    'Specialist in Apastamba Grihya Sutra traditions'],

    ritualExpertise: [
    { name: 'Gruhapravesam', description: 'Complete Vastu Shanti and housewarming ceremony with all 16 rites', icon: '🏠' },
    { name: 'Vivah Sanskar', description: 'Full Vedic wedding with Saptapadi, Kanyadan, and all samskaras', icon: '💍' },
    { name: 'Rudrabhishek', description: 'Sacred Shiva abhisheka with Rudra Ashtadhyayi chanting', icon: '🕉️' },
    { name: 'Satyanarayan Puja', description: 'Vrat Katha with Panchamrita abhisheka and prasad distribution', icon: '🪔' },
    { name: 'Navagraha Shanti', description: 'Planetary appeasement with individual homas for each graha', icon: '⭐' },
    { name: 'Namakarana', description: 'Auspicious naming ceremony with Jatakarma and Nishkramana rites', icon: '👶' }],

    reviews: [
    { name: 'Priya Venkataraman', location: 'Bengaluru', rating: 5, text: 'Pandit Raghavendra performed our Gruhapravesam with such devotion and clarity. He explained every step to our family, including the children. The energy in our home has been wonderful since.', ritual: 'Gruhapravesam', date: 'March 2026' },
    { name: 'Suresh & Kavitha Rao', location: 'Mysuru', rating: 5, text: 'Our wedding was everything we dreamed of. Pandit ji took time to understand our family traditions and incorporated them beautifully. His Sanskrit chanting was deeply moving.', ritual: 'Vivah Sanskar', date: 'February 2026' },
    { name: 'Anand Krishnamurthy', location: 'Bengaluru', rating: 5, text: 'The Rudrabhishek was performed with great precision and devotion. Pandit ji arrived on time, brought all the required materials, and the entire ceremony was completed within the promised duration.', ritual: 'Rudrabhishek', date: 'January 2026' },
    { name: 'Meena Iyer', location: 'Chennai', rating: 4, text: 'Very knowledgeable and punctual. The Satyanarayan Puja was conducted beautifully. Would have given 5 stars but the ceremony ran slightly over time.', ritual: 'Satyanarayan Puja', date: 'December 2025' }],

    gallery: [
    { src: "https://images.unsplash.com/photo-1715281120429-323a3dd57ee8", alt: 'Ganesh Puja ceremony with decorated idol and flowers' },
    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_11042383a-1776930933933.png", alt: 'Gruhapravesam ceremony with traditional decorations' },
    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef8f82b3-1765028486047.png", alt: 'Rudrabhishek with Shiva lingam and milk offerings' },
    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_15904716c-1767725167925.png", alt: 'Vivah Sanskar wedding ceremony around sacred fire' },
    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_151f8b4e4-1765087240334.png", alt: 'Lakshmi Puja with decorated idol and diyas' },
    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f11e9770-1785773643323.png", alt: 'Havan Homa sacred fire ritual' }],

    availableSlots: {
      '2026-08-05': ['6:00 AM', '9:00 AM', '5:00 PM'],
      '2026-08-06': ['7:00 AM', '10:00 AM'],
      '2026-08-07': [],
      '2026-08-08': ['6:00 AM', '8:00 AM', '3:00 PM', '6:00 PM'],
      '2026-08-09': ['7:00 AM', '11:00 AM'],
      '2026-08-10': ['6:00 AM', '9:00 AM', '5:00 PM', '7:00 PM'],
      '2026-08-12': ['8:00 AM'],
      '2026-08-13': ['6:00 AM', '7:00 AM', '9:00 AM'],
      '2026-08-14': [],
      '2026-08-15': ['6:00 AM', '10:00 AM', '4:00 PM'],
      '2026-08-16': ['7:00 AM', '3:00 PM'],
      '2026-08-17': ['6:00 AM', '8:00 AM']
    }
  }
};

// Default details for priests without specific data
const DEFAULT_DETAILS = PRIEST_DETAILS['raghavendra-sharma'];

const availabilityColor: Record<string, string> = {
  'Available Today': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Available This Week': 'bg-amber-100 text-amber-700 border-amber-200',
  'Booked': 'bg-red-50 text-red-500 border-red-200'
};

function StarRow({ rating }: {rating: number;}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) =>
      <Star key={s} size={13} fill={s <= Math.round(rating) ? '#D4AF37' : 'none'} stroke="#D4AF37" />
      )}
    </div>);

}

function AvailabilityCalendar({ slots }: {slots: Record<string, string[]>;}) {
  const today = new Date(2026, 7, 3); // Aug 3 2026
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 7, 1));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const selectedSlots = selectedDate ? slots[selectedDate] ?? [] : [];

  return (
    <div className="bg-white rounded-3xl border border-gold/20 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-base font-semibold text-maroon flex items-center gap-2">
          <Calendar size={16} className="text-saffron" />
          Availability Calendar
        </h2>
        <div className="flex items-center gap-1">
          <button onClick={prevMonth} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gold/10 transition-colors text-maroon/60">
            <ChevronLeft size={15} />
          </button>
          <span className="text-sm font-semibold text-maroon px-1 tabular-nums">
            {MONTHS[month]} {year}
          </span>
          <button onClick={nextMonth} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gold/10 transition-colors text-maroon/60">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {DAYS.map((d) =>
        <div key={d} className="text-center text-[10px] font-semibold text-maroon/40 py-1">{d}</div>
        )}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {[...Array(firstDay)].map((_, i) => <div key={`e-${i}`} />)}
        {[...Array(daysInMonth)].map((_, i) => {
          const day = i + 1;
          const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isPast = new Date(year, month, day) < today;
          const hasSlots = slots[dateKey]?.length > 0;
          const isSelected = selectedDate === dateKey;
          const isUnavailable = slots[dateKey]?.length === 0 && dateKey in slots;
          const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

          return (
            <button
              key={day}
              disabled={isPast || isUnavailable}
              onClick={() => {setSelectedDate(dateKey);setSelectedSlot(null);}}
              className={`relative aspect-square flex flex-col items-center justify-center rounded-xl text-xs font-medium transition-all duration-200 ${
              isSelected ?
              'saffron-gradient text-white shadow-md scale-105' :
              isPast || isUnavailable ?
              'text-maroon/20 cursor-not-allowed' :
              hasSlots ?
              'text-maroon hover:bg-saffron/10 hover:text-saffron cursor-pointer' : 'text-maroon/50 hover:bg-cream cursor-pointer'} ${
              isToday && !isSelected ? 'ring-2 ring-saffron/40' : ''}`}>
              
              {day}
              {hasSlots && !isSelected && !isPast &&
              <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-saffron" />
              }
            </button>);

        })}
      </div>

      <div className="flex items-center gap-4 mt-3 text-[10px] text-maroon/50">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-saffron inline-block" />Available</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-maroon/20 inline-block" />Unavailable</span>
      </div>

      {selectedDate &&
      <div className="mt-4 pt-4 border-t border-gold/20">
          <div className="text-xs font-semibold text-maroon/60 mb-3">
            Available slots — {selectedDate}
          </div>
          {selectedSlots.length > 0 ?
        <div className="grid grid-cols-3 gap-2">
              {selectedSlots.map((slot) =>
          <button
            key={slot}
            onClick={() => setSelectedSlot(slot)}
            className={`text-xs font-medium py-2 px-2 rounded-xl border transition-all duration-200 ${
            selectedSlot === slot ?
            'saffron-gradient text-white border-transparent shadow-sm' :
            'border-gold/30 text-maroon hover:border-saffron hover:text-saffron bg-cream'}`
            }>
            
                  {slot}
                </button>
          )}
            </div> :

        <div className="text-center py-4 text-xs text-maroon/40 bg-cream rounded-xl">
              Fully booked on this date
            </div>
        }
        </div>
      }
    </div>);

}

export default function PriestDetailPage() {
  const params = useParams();
  const id = params?.id as string;
  const priest = PRIESTS_DATA.find((p) => p.id === id) ?? PRIESTS_DATA[0];
  const details = PRIEST_DETAILS[id] ?? DEFAULT_DETAILS;
  const [activeGalleryIdx, setActiveGalleryIdx] = useState(0);

  const ratingBreakdown = useMemo(() => {
    const counts = [0, 0, 0, 0, 0];
    details.reviews.forEach((r) => {if (r.rating >= 1 && r.rating <= 5) counts[r.rating - 1]++;});
    return counts.reverse();
  }, [details.reviews]);

  return (
    <div className="min-h-screen bg-ivory">
      <HomeNavbar />

      {/* Hero Banner */}
      <div className="pt-16" style={{ background: 'linear-gradient(135deg, #500A0A 0%, #7B1C1C 50%, #9B3A1A 100%)' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link href="/priests" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors mb-4">
            <ArrowLeft size={15} />
            Back to Priests
          </Link>

          <div className="flex flex-col sm:flex-row gap-5 items-start">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-3xl overflow-hidden border-4 border-white/30 shadow-2xl">
                <img
                  src={priest.image}
                  alt={priest.alt}
                  className="w-full h-full object-cover object-top"
                  onError={(e) => {(e.target as HTMLImageElement).src = '/assets/images/no_image.png';}} />
                
              </div>
              {priest.verified &&
              <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                  <CheckCircle2 size={14} className="text-white" />
                </div>
              }
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="font-display text-2xl md:text-3xl font-bold text-white">{priest.name}</h1>
                <span className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${availabilityColor[priest.availability]}`}>
                  {priest.availability}
                </span>
              </div>
              <p className="text-white/70 text-sm font-body mb-3">{priest.title}</p>

              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-xs bg-white/10 border border-white/20 text-amber-200 px-2.5 py-1 rounded-full font-medium">{priest.tradition}</span>
                <span className="text-xs bg-white/10 border border-white/20 text-white/80 px-2.5 py-1 rounded-full">{priest.veda}</span>
                <span className="text-xs bg-white/10 border border-white/20 text-white/80 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Clock size={10} />{priest.experience}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
                <div className="flex items-center gap-1.5">
                  <Star size={14} fill="#D4AF37" stroke="#D4AF37" />
                  <span className="font-bold text-white">{priest.rating}</span>
                  <span>({priest.reviewCount} reviews)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin size={13} className="text-saffron" />
                  {priest.location}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 flex-shrink-0">
              <button className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-medium px-3 py-2 rounded-xl transition-all">
                <Video size={13} />
                Consult
              </button>
              <button className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-medium px-3 py-2 rounded-xl transition-all">
                <Phone size={13} />
                Call
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 mt-6 pb-6">
            {[
            { value: priest.experience, label: 'Experience' },
            { value: `${priest.ritualsCount}+`, label: 'Rituals Done' },
            { value: `${priest.reviewCount}`, label: 'Reviews' }].
            map((stat) =>
            <div key={stat.label} className="text-center bg-white/10 border border-white/10 rounded-2xl py-3">
                <div className="font-display text-xl font-bold text-white tabular-nums">{stat.value}</div>
                <div className="text-xs text-white/50 font-body mt-0.5">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 md:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">

            {/* Biography */}
            <section className="bg-white rounded-3xl border border-gold/20 shadow-sm p-6">
              <h2 className="font-display text-lg font-bold text-maroon mb-4 flex items-center gap-2">
                <BookOpen size={18} className="text-saffron" />
                Biography
              </h2>
              <p className="text-sm text-maroon/70 font-body leading-relaxed">{details.biography}</p>
            </section>

            {/* Qualifications */}
            <section className="bg-white rounded-3xl border border-gold/20 shadow-sm p-6">
              <h2 className="font-display text-lg font-bold text-maroon mb-4 flex items-center gap-2">
                <Award size={18} className="text-saffron" />
                Qualifications
              </h2>
              <ul className="space-y-3">
                {details.qualifications.map((q, i) =>
                <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full saffron-gradient flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 size={11} className="text-white" />
                    </div>
                    <span className="text-sm text-maroon/70 font-body">{q}</span>
                  </li>
                )}
              </ul>
            </section>

            {/* Ritual Expertise */}
            <section className="bg-white rounded-3xl border border-gold/20 shadow-sm p-6">
              <h2 className="font-display text-lg font-bold text-maroon mb-4 flex items-center gap-2">
                <Flame size={18} className="text-saffron" />
                Ritual Expertise
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {details.ritualExpertise.map((r) =>
                <div key={r.name} className="flex items-start gap-3 bg-cream rounded-2xl p-3.5 border border-gold/15">
                    <span className="text-2xl flex-shrink-0">{r.icon}</span>
                    <div>
                      <div className="text-sm font-bold text-maroon">{r.name}</div>
                      <div className="text-xs text-maroon/55 font-body mt-0.5 leading-snug">{r.description}</div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Languages */}
            <section className="bg-white rounded-3xl border border-gold/20 shadow-sm p-6">
              <h2 className="font-display text-lg font-bold text-maroon mb-4 flex items-center gap-2">
                <Languages size={18} className="text-saffron" />
                Languages
              </h2>
              <div className="flex flex-wrap gap-2">
                {priest.languages.map((lang) =>
                <span key={lang} className="bg-cream text-maroon border border-gold/30 text-sm font-medium px-4 py-2 rounded-full">
                    {lang}
                  </span>
                )}
              </div>
            </section>

            {/* Gallery */}
            <section className="bg-white rounded-3xl border border-gold/20 shadow-sm p-6">
              <h2 className="font-display text-lg font-bold text-maroon mb-4 flex items-center gap-2">
                <Camera size={18} className="text-saffron" />
                Gallery
              </h2>
              {/* Main image */}
              <div className="relative h-56 md:h-72 rounded-2xl overflow-hidden mb-3 bg-cream">
                <img
                  src={details.gallery[activeGalleryIdx]?.src}
                  alt={details.gallery[activeGalleryIdx]?.alt}
                  className="w-full h-full object-cover transition-all duration-500"
                  onError={(e) => {(e.target as HTMLImageElement).src = '/assets/images/no_image.png';}} />
                
                <button
                  onClick={() => setActiveGalleryIdx((prev) => (prev - 1 + details.gallery.length) % details.gallery.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-colors">
                  
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setActiveGalleryIdx((prev) => (prev + 1) % details.gallery.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-colors">
                  
                  <ChevronRight size={16} />
                </button>
              </div>
              {/* Thumbnails */}
              <div className="grid grid-cols-6 gap-2">
                {details.gallery.map((img, i) =>
                <button
                  key={i}
                  onClick={() => setActiveGalleryIdx(i)}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                  activeGalleryIdx === i ? 'border-saffron shadow-md scale-105' : 'border-transparent opacity-60 hover:opacity-100'}`
                  }>
                  
                    <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                    onError={(e) => {(e.target as HTMLImageElement).src = '/assets/images/no_image.png';}} />
                  
                  </button>
                )}
              </div>
            </section>

            {/* Reviews */}
            <section className="bg-white rounded-3xl border border-gold/20 shadow-sm p-6">
              <h2 className="font-display text-lg font-bold text-maroon mb-5 flex items-center gap-2">
                <Star size={18} className="text-saffron" />
                Reviews
              </h2>

              {/* Rating Summary */}
              <div className="flex items-center gap-6 mb-6 p-4 bg-cream rounded-2xl border border-gold/15">
                <div className="text-center">
                  <div className="font-display text-4xl font-bold text-maroon tabular-nums">{priest.rating}</div>
                  <StarRow rating={priest.rating} />
                  <div className="text-xs text-maroon/50 mt-1">{priest.reviewCount} reviews</div>
                </div>
                <div className="flex-1 space-y-1.5">
                  {ratingBreakdown.map((count, i) => {
                    const stars = 5 - i;
                    const pct = details.reviews.length > 0 ? count / details.reviews.length * 100 : 0;
                    return (
                      <div key={stars} className="flex items-center gap-2">
                        <span className="text-xs text-maroon/50 w-3 tabular-nums">{stars}</span>
                        <div className="flex-1 h-1.5 bg-gold/20 rounded-full overflow-hidden">
                          <div className="h-full bg-saffron rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-maroon/40 w-4 tabular-nums">{count}</span>
                      </div>);

                  })}
                </div>
              </div>

              {/* Review Cards */}
              <div className="space-y-4">
                {details.reviews.map((review, i) =>
                <div key={i} className="border border-gold/15 rounded-2xl p-4 bg-cream/40">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full saffron-gradient flex items-center justify-center text-white text-xs font-bold">
                            {review.name[0]}
                          </div>
                          <div>
                            <div className="text-sm font-bold text-maroon">{review.name}</div>
                            <div className="text-xs text-maroon/40">{review.location} · {review.date}</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <StarRow rating={review.rating} />
                        <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">{review.ritual}</span>
                      </div>
                    </div>
                    <div className="relative pl-4">
                      <Quote size={14} className="absolute left-0 top-0 text-saffron/40" />
                      <p className="text-sm text-maroon/65 font-body leading-relaxed">{review.text}</p>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Right Column — Sticky Booking Panel */}
          <div className="space-y-5">
            {/* Availability Calendar */}
            <AvailabilityCalendar slots={details.availableSlots} />

            {/* Book CTA */}
            <div className="bg-white rounded-3xl border border-gold/20 shadow-sm p-5 sticky top-24">
              <div className="text-center mb-4">
                <div className="text-xs text-maroon/50 mb-1">Starting from</div>
                <div className="font-display text-2xl font-bold text-maroon">₹{priest.startingFee.toLocaleString()}</div>
                <div className="text-xs text-maroon/40">per ceremony</div>
              </div>

              <Link
                href="/booking-flow"
                className="block w-full text-center bg-maroon hover:bg-maroon/90 text-white font-bold text-base py-4 rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 mb-3"
                style={{ background: 'linear-gradient(135deg, #500A0A 0%, #7B1C1C 100%)' }}>
                
                🪔 Book This Priest
              </Link>

              <div className="flex gap-2">
                <button className="flex-1 flex items-center justify-center gap-1.5 border border-gold/30 text-maroon text-sm font-medium py-2.5 rounded-xl hover:border-saffron/40 hover:bg-saffron/5 transition-all">
                  <Video size={14} />
                  Consult
                </button>
                <button className="flex-1 flex items-center justify-center gap-1.5 border border-gold/30 text-maroon text-sm font-medium py-2.5 rounded-xl hover:border-saffron/40 hover:bg-saffron/5 transition-all">
                  <Phone size={14} />
                  Call
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-gold/15 space-y-2">
                {[
                { icon: '✅', text: 'Background verified priest' },
                { icon: '🔒', text: 'Secure payment via Yagnick' },
                { icon: '↩️', text: 'Free rescheduling up to 24h' }].
                map((item) =>
                <div key={item.text} className="flex items-center gap-2 text-xs text-maroon/55">
                    <span>{item.icon}</span>
                    <span>{item.text}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <HomeBottomNav />
    </div>);

}